// © Microsoft Corporation. All rights reserved.

import { captureJssGeneratedClasses } from "@msnews/render-cache";
import { registerResetCallback } from "@msnews/core";

/**
 * Map which is used to store DesignSystem to GenerateClassName Callback.
 * Serialized designSystem is used as the key to ensure designSystemIndex is incremented if the designSystem is updated.
 * Weakmap is not used as identical designSystem objects should not be treated as different designSystems, an example is PivotContent.tsx progressive hydration.
 * Objects potentially garbage collected in WeakMap will also result in designSystemIndexMap being out of sync.
 */
let designSystemMap = new Map<string, any>();

/**
 * Map which will store number of DesignSystem appearances with same name.
 */
let designSystemIndexMap = new Map<string, number>();

/**
 * Register a callback to reset the designSystem globals.
 * The callback is registered once when the module is loaded and invoked once on the server side at the beginning of each request.
 */
registerResetCallback(() => {
    designSystemMap = new Map<any, any>();
    designSystemIndexMap = new Map<string, number>();
});

/**
 * JSS callback function that defines the class name generation logic
 * The default jss manager class name generation logic increments counter based on order
 * of how the experiences are rendered. We cannot guarantee the order due to async nature
 * of experiences which potentially causes different class names on every reload/refresh.
 * Instead here with this callback, we start the counter from 1 per rule to ensure class
 * names stays consistent irrespective of when they are rendered.
 *
 * Design system is taken into account since a change in it can result in potential different
 * styling being needed even if that rule was already used in a previous design system.
 *
 * For example:
 * For TopSites, with this override the class name would be generated as "topSites-DS-default1-1"
 * (jss manager by default would have generated it something like "topSites-0-1-305")
 * If the design system were to be updated and the same rule would be passed then you would
 * get topsites-DS-default2-1.
 */
export function createGenerateClassName(designSystem: any): (rule: any, sheet: any) => string {
    if (!designSystem) {
        return;
    }

    const dsKey = JSON.stringify(designSystem);
    let callback = designSystemMap.get(dsKey);

    if (!callback) {
        const dsName = designSystem.name || "unknown";
        const dsIndex = designSystemIndexMap.get(dsName) + 1 || 1;
        const rulesMap: any = {};
        callback = (rule: any, sheet: any) => {
            const key = rule.key;

            if (!rulesMap[key]) {
                rulesMap[key] = 1;
            } else {
                rulesMap[key]++;
            }

            const name = `DS-${dsName}${dsIndex}-${rulesMap[key]}`;
            captureJssGeneratedClasses(key, name);

            return `${key}-${name}`;
        };

        designSystemMap.set(dsKey, callback);
        designSystemIndexMap.set(dsName, dsIndex);
    }

    return callback;
}